// Helper to generate printable practice sheet content per activity
// Each activity type is responsible for formatting its own practice content here.

// WORD_MEANING: one word per line -> language word followed by _____________
const formatWordMeaningPractice = (parsed) => {
  const words = parsed?.words || [];
  if (!words.length) return 'No words available for this activity.';

  let text = '';
  words.forEach((w, idx) => {
    const word = w.languageWord || '';
    text += `${idx + 1}. ${word}  ______________________________\n\n`;
  });
  return text.trimEnd();
};

// CHANGE_GENDERS answers:
// Show both male and female language words for each pair
// Example: "1. ರಾಜ  ->  ರಾಣಿ"
const formatChangeGendersAnswers = (parsed) => {
  const pairs = Array.isArray(parsed?.pairs) ? parsed.pairs : [];
  if (!pairs.length) return 'No words available for this activity.';

  // Filter to pairs that have at least one language word
  const valid = pairs.filter(p => (p.maleLanguageWord && p.maleLanguageWord.trim()) || (p.femaleLanguageWord && p.femaleLanguageWord.trim()));
  if (!valid.length) return 'No words available for this activity.';

  let text = '';
  valid.forEach((p, idx) => {
    const male = (p.maleLanguageWord || '').trim();
    const female = (p.femaleLanguageWord || '').trim();

    if (male && female) {
      text += `${idx + 1}. ${male}  ->  ${female}\n\n`;
    } else if (male) {
      text += `${idx + 1}. ${male}  ->  (female not provided)\n\n`;
    } else if (female) {
      text += `${idx + 1}. (male not provided)  ->  ${female}\n\n`;
    }
  });

  return text.trimEnd();
};

// CHANGE_GENDERS practice:
// For each pair, randomly choose either male or female language word and add a blank
// Example: "1. ರಾಜ  ______________________________"
const formatChangeGendersPractice = (parsed) => {
  const pairs = Array.isArray(parsed?.pairs) ? parsed.pairs : [];
  if (!pairs.length) return 'No words available for this activity.';

  // Filter to pairs that have at least one language word
  const valid = pairs.filter(p => (p.maleLanguageWord && p.maleLanguageWord.trim()) || (p.femaleLanguageWord && p.femaleLanguageWord.trim()));
  if (!valid.length) return 'No words available for this activity.';

  let text = '';
  valid.forEach((p, idx) => {
    const hasMale = p.maleLanguageWord && p.maleLanguageWord.trim();
    const hasFemale = p.femaleLanguageWord && p.femaleLanguageWord.trim();

    let questionWord = '';
    if (hasMale && hasFemale) {
      const useMale = Math.random() < 0.5;
      questionWord = useMale ? p.maleLanguageWord : p.femaleLanguageWord;
    } else if (hasMale) {
      questionWord = p.maleLanguageWord;
    } else if (hasFemale) {
      questionWord = p.femaleLanguageWord;
    }

    text += `${idx + 1}. ${questionWord}  ______________________________\n\n`;
  });

  return text.trimEnd();
};

// MAKE_SENTENCES practice:
// One sentence per line -> language word followed by dotted/underscore line
const formatMakeSentencesPractice = (parsed) => {
  const sentences = parsed?.sentences || [];
  if (!sentences.length) return 'No sentences available for this activity.';

  let text = '';
  sentences.forEach((s, idx) => {
    const word = s.languageWord || '';
    text += `${idx + 1}. ${word}  ______________________________\n\n`;
  });
  return text.trimEnd();
};

// TENSES practice:
// List all English sentences asking the user to translate to the language
const formatTensesPractice = (parsed) => {
  const groups = Array.isArray(parsed?.groups) ? parsed.groups : [];
  const sentences = groups.flatMap(g => Array.isArray(g.sentences) ? g.sentences : []);
  const valid = sentences.filter(s => s && (s.englishText || '').trim());

  if (!valid.length) return 'No sentences available for this activity.';

  let text = '';
  valid.forEach((s, idx) => {
    const en = s.englishText || '';
    text += `${idx + 1}. ${en}\n`;
    text += '   Translate to language: ________________________________\n\n\n';
  });

  return text.trimEnd();
};

// GENDERS answers: list words categorized under each gender
const formatGendersAnswers = (parsed) => {
  const words = parsed?.words || [];
  if (!words.length) return 'No words available for this activity.';

  // Group words by display gender (prefer Kannada, fallback to English)
  const groups = {};
  words.forEach((w) => {
    const genderKey = w.genderKannada || w.genderEnglish || 'Unknown';
    const displayWord = w.word || w.englishWord || '';
    if (!displayWord) return;
    if (!groups[genderKey]) {
      groups[genderKey] = [];
    }
    groups[genderKey].push(displayWord);
  });

  const genderKeys = Object.keys(groups);
  if (!genderKeys.length) return 'No genders configured for this activity.';

  let text = '';
  genderKeys.forEach((g, index) => {
    if (index > 0) {
      text += '\n';
    }
    text += `${g}:\n`;
    groups[g].forEach((word, idx) => {
      text += `  ${idx + 1}. ${word}\n`;
    });
  });

  return text.trimEnd();
};

// NUMBERS: jumbled English numbers with blanks for language number and language word
const formatNumbersPractice = (parsed) => {
  const numbers = Array.isArray(parsed?.numbers) ? parsed.numbers : [];
  if (!numbers.length) return 'No numbers available for this activity.';

  // Filter to those that have an English number
  const valid = numbers.filter(n => n && n.englishNumber);
  if (!valid.length) return 'No numbers available for this activity.';

  // Shuffle for practice
  const shuffled = [...valid].sort(() => Math.random() - 0.5);

  // Header row
  let text = '\n\n       Kannada Number    Kannada Number Word\n\n';

  shuffled.forEach((n) => {
    // Example: `1. 5  _____  __________________________`
    text += `${n.englishNumber}.         _____           __________________________\n\n\n`;
  });

  return text;
};

// LONG_QUESTION_ANSWERS:
// Uses nested structure: questions[{ question: { languageText/englishText }, answer: { sentences: [...] } }]
// Print each question followed by 6 lines of empty space
const formatLongQuestionAnswersPractice = (parsed) => {
  const questions = parsed?.questions || [];
  if (!questions.length) return 'No questions available for this activity.';

  let text = '';
  questions.forEach((qItem, idx) => {
    const q = qItem?.question || {};
    const question = q.languageText || q.englishText || '';
    text += `${idx + 1}. ${question}\n`;
    for (let i = 0; i < 6; i++) {
      text += '\n';
    }
  });
  // Don't trim here so we preserve space after the last question
  return text;
};

// WHO_SAID_TO_WHOM:
// first line: the sentence (languageText)
// second line: Who Said: ______________
// third line: To Whom: ______________
const formatWhoSaidToWhomPractice = (parsed) => {
  const dialogues = parsed?.dialogues || [];
  if (!dialogues.length) return 'No dialogues available for this activity.';

  let text = '';
  dialogues.forEach((d, idx) => {
    const sentence = d.languageText || '';
    text += `${idx + 1}. ${sentence}\n\n`;
    text += `   Who Said: ______________________________\n\n`;
    text += `   To Whom: ______________________________\n\n\n\n`;
  });
  return text.trimEnd();
};

// QUESTION_ANSWERS:
// Print each question followed by N lines of empty space.
const formatQuestionAnswersPractice = (parsed, linesPerAnswer = 3) => {
  const questions = parsed?.questions || [];
  if (!questions.length) return 'No questions available for this activity.';

  let text = '';
  questions.forEach((q, idx) => {
    const question = q.questionLanguage || q.questionEnglish || '';
    text += `${idx + 1}. ${question}\n`;
    for (let i = 0; i < linesPerAnswer; i++) {
      text += '\n';
    }
  });
  // Don't trim here so we preserve space after the last question
  return text;
};

// CREATIVE_WRITING:
// Write on this topic: "Name of chapter" followed by 15 lines of empty space
const formatCreativeWritingPractice = (chapterTitle) => {
  const title = chapterTitle || 'this topic';
  let text = '';
  text += `Write a few sentences on this topic: "${title}"\n`;
  for (let i = 0; i < 15; i++) {
    text += '\n';
  }
  return text.trimEnd();
};

// DUAL_MEANING: one word per line -> language word followed by two blanks
const formatDualMeaningPractice = (parsed) => {
  const words = parsed?.words || [];
  if (!words.length) return 'No words available for this activity.';

  let text = '';
  words.forEach((w, idx) => {
    const word = w.languageWord || '';
    text += `${idx + 1}. ${word}  ____________________    ____________________\n\n`;
  });
  return text.trimEnd();
};

// MATCH: print keyword and value words in 2 columns, jumbled order for value words
const formatMatchPractice = (parsed) => {
  const pairs = parsed?.wordPairs || [];
  if (!pairs.length) return 'No word pairs available for this activity.';

  const left = pairs.map((p) => p.keyWord || '').filter(Boolean);
  const right = pairs.map((p) => p.valueWord || '').filter(Boolean);

  if (!left.length || !right.length) return 'No word pairs available for this activity.';

  // Shuffle right column
  const shuffledRight = [...right];
  for (let i = shuffledRight.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffledRight[i], shuffledRight[j]] = [shuffledRight[j], shuffledRight[i]];
  }

  let text = '';
  const maxRows = Math.max(left.length, shuffledRight.length);
  for (let i = 0; i < maxRows; i++) {
    const l = left[i] || '';
    const r = shuffledRight[i] || '';
    // Simple spacing between columns for printing
    text += `${i + 1}. ${l.padEnd(20, ' ')}  ${r}\n`;
  }

  return text.trimEnd();
};

// OPPOSITES: one word per line -> language word followed by " X  ____________"
const formatOppositesPractice = (parsed) => {
  const words = parsed?.words || [];
  if (!words.length) return 'No words available for this activity.';

  let text = '';
  words.forEach((w, idx) => {
    const word = w.word || '';
    text += `${idx + 1}. ${word}  X  ______________________________\n\n`;
  });
  return text.trimEnd();
};

// SINGULAR_PLURAL: heading "Change into singular/plural"
// For each entry, randomly show either singular or plural form and ask to convert
const formatSingularPluralPractice = (parsed) => {
  const words = parsed?.words || [];
  if (!words.length) return 'No words available for this activity.';

  // Filter to rows that have at least one of singular/plural filled
  const valid = words.filter(w => (w.singularWord && w.singularWord.trim()) || (w.pluralWord && w.pluralWord.trim()));
  if (!valid.length) return 'No words available for this activity.';

  let text = '';
  text += 'Change into singular/plural\n\n';

  valid.forEach((w, idx) => {
    const hasSingular = w.singularWord && w.singularWord.trim();
    const hasPlural = w.pluralWord && w.pluralWord.trim();

    // Decide which side to show: if both exist, randomly pick one; otherwise use whichever is present
    let questionWord = '';
    if (hasSingular && hasPlural) {
      const showSingular = Math.random() < 0.5;
      questionWord = showSingular ? w.singularWord : w.pluralWord;
    } else if (hasSingular) {
      questionWord = w.singularWord;
    } else if (hasPlural) {
      questionWord = w.pluralWord;
    }

    text += `${idx + 1}. ${questionWord}  ______________________________\n\n`;
  });

  return text.trimEnd();
};

// GENDERS: categorize words under gender headings
// - Heading: "Categorize as per gender"
// - Next line(s): all words in jumbled order
// - Then one section per gender with blank space
const formatGendersPractice = (parsed) => {
  const words = parsed?.words || [];
  if (!words.length) return 'No words available for this activity.';

  // Collect all distinct display genders (prefer Kannada, fallback to English)
  const rawGenders = words
    .map(w => w.genderKannada || w.genderEnglish)
    .filter(Boolean);
  const uniqueGenders = Array.from(new Set(rawGenders));

  if (!uniqueGenders.length) return 'No genders configured for this activity.';

  // Jumbled list of words
  const shuffledWords = [...words]
    .filter(w => w && w.word)
    .sort(() => Math.random() - 0.5);

  let text = '';
  text += 'Categorize as per gender\n\n';

  if (shuffledWords.length) {
    text += 'Words:\n';
    shuffledWords.forEach((w, idx) => {
      text += `${idx + 1}. ${w.word}\n`;
    });
  } else {
    text += 'Words: (none configured)\n';
  }

  text += '\n';

  // One heading per gender with space for students to write words
  uniqueGenders.forEach((g, index) => {
    if (index > 0) {
      text += '\n';
    }
    text += `${g}:\n`;
    // Add multiple blank lines for answers
    text += '  ______________________________\n\n';
    text += '  ______________________________\n\n';
    text += '  ______________________________\n\n';
  });

  return text.trimEnd();
};

const formatFillInBlankMcqPractice = (parsed) => {
  const items = parsed?.items || [];
  if (!items.length) return 'No sentences available for this activity.';

  let text = '';
  items.forEach((item, idx) => {
    const options = Array.isArray(item.options) ? item.options : [];
    text += `${idx + 1}. ${item.sentence || ''}\n`;
    if (options.length) {
      options.forEach((opt, optIndex) => {
        const label = String.fromCharCode(65 + optIndex); // A, B, C, ...
        text += `   (${label}) ${opt}\n`;
      });
    }
    text += '\n';
  });
  return text.trimEnd();
};

// ========================= ANSWER SHEET HELPERS =========================

const formatWordMeaningAnswers = (parsed) => {
  const words = parsed?.words || [];
  if (!words.length) return 'No words available for this activity.';

  let text = '';
  words.forEach((w, idx) => {
    text += `${idx + 1}. ${w.languageWord || ''} = ${w.languageMeaning || ''}\n\n`;
  });
  return text.trimEnd();
};

// MAKE_SENTENCES answers:
// language word and the full language sentence
const formatMakeSentencesAnswers = (parsed) => {
  const sentences = parsed?.sentences || [];
  if (!sentences.length) return 'No sentences available for this activity.';

  let text = '';
  sentences.forEach((s, idx) => {
    const word = s.languageWord || '';
    const sentence = s.languageSentence || '';
    text += `${idx + 1}. ${word} = ${sentence}\n\n`;
  });
  return text.trimEnd();
};

const formatDualMeaningAnswers = (parsed) => {
  const words = parsed?.words || [];
  if (!words.length) return 'No words available for this activity.';

  let text = '';
  words.forEach((w, idx) => {
    const m1 = w.meaning1 || '';
    const m2 = w.meaning2 || '';
    text += `${idx + 1}. ${w.languageWord || ''} = ${m1} , ${m2}\n\n`;
  });
  return text.trimEnd();
};

const formatMatchAnswers = (parsed) => {
  const pairs = parsed?.wordPairs || [];
  if (!pairs.length) return 'No word pairs available for this activity.';

  let text = '';
  pairs.forEach((p, idx) => {
    text += `${idx + 1}. ${p.keyWord || ''} -> ${p.valueWord || ''}\n`;
  });
  return text.trimEnd();
};

const formatOppositesAnswers = (parsed) => {
  const words = parsed?.words || [];
  if (!words.length) return 'No words available for this activity.';

  let text = '';
  words.forEach((w, idx) => {
    text += `${idx + 1}. ${w.word || ''}  X  ${w.opposite || ''}\n\n`;
  });
  return text.trimEnd();
};

// SINGULAR_PLURAL answers: show both forms so teacher sees the conversion
// We list each row once as: singular -> plural (falling back to whichever is available)
const formatSingularPluralAnswers = (parsed) => {
  const words = parsed?.words || [];
  if (!words.length) return 'No words available for this activity.';

  // Filter to rows that have at least one of singular/plural filled
  const valid = words.filter(w => (w.singularWord && w.singularWord.trim()) || (w.pluralWord && w.pluralWord.trim()));
  if (!valid.length) return 'No words available for this activity.';

  let text = '';
  valid.forEach((w, idx) => {
    const singular = (w.singularWord || '').trim();
    const plural = (w.pluralWord || '').trim();

    // If one side missing, just show the one we have
    if (singular && plural) {
      text += `${idx + 1}. ${singular} -> ${plural}\n\n`;
    } else if (singular) {
      text += `${idx + 1}. ${singular} -> (plural not provided)\n\n`;
    } else if (plural) {
      text += `${idx + 1}. (singular not provided) -> ${plural}\n\n`;
    }
  });

  return text.trimEnd();
};

const formatWhoSaidToWhomAnswers = (parsed) => {
  const dialogues = parsed?.dialogues || [];
  if (!dialogues.length) return 'No dialogues available for this activity.';

  let text = '';
  dialogues.forEach((d, idx) => {
    text += `${idx + 1}. ${d.languageText || ''}\n`;
    text += `   Who Said: ${d.whoSaid || ''}\n`;
    text += `   To Whom: ${d.toWhom || ''}\n\n`;
  });
  return text.trimEnd();
};

const formatQuestionAnswersAnswers = (parsed) => {
  const questions = parsed?.questions || [];
  if (!questions.length) return 'No questions available for this activity.';

  let text = '';
  questions.forEach((q, idx) => {
    const question = q.questionLanguage || q.questionEnglish || '';
    const answer = q.answerLanguage || q.answerEnglish || '';
    text += `${idx + 1}. Q: ${question}\n`;
    text += `   A: ${answer}\n\n`;
  });
  return text.trimEnd();
};

const formatLongQuestionAnswersAnswers = (parsed) => {
  const questions = parsed?.questions || [];
  if (!questions.length) return 'No questions available for this activity.';

  let text = '';
  questions.forEach((qItem, idx) => {
    const q = qItem?.question || {};
    const question = q.languageText || q.englishText || '';

    const sentences = qItem?.answer?.sentences || [];
    const answerParts = sentences
      .map(s => s.languageText || s.englishText || '')
      .filter(Boolean);
    const answer = answerParts.join(' ');

    text += `${idx + 1}. Q: ${question}\n`;
    text += `   A: ${answer}\n\n`;
  });
  return text.trimEnd();
};

const formatFillInBlankMcqAnswers = (parsed) => {
  const items = parsed?.items || [];
  if (!items.length) return 'No sentences available for this activity.';

  let text = '';
  items.forEach((item, idx) => {
    text += `${idx + 1}. ${item.sentence || ''}\n`;
    text += `   Answer: ${item.correctOption || ''}\n\n`;
  });
  return text.trimEnd();
};

// NUMBERS answers: show English = language number, language word
const formatNumbersAnswers = (parsed) => {
  const numbers = Array.isArray(parsed?.numbers) ? parsed.numbers : [];
  if (!numbers.length) return 'No numbers available for this activity.';

  let text = '';
  numbers.forEach((n) => {
    const en = n.englishNumber || '';
    const langNum = n.languageNumber || '';
    const langWord = n.languageWordNumber || '';
    text += `${en} = ${langNum} , ${langWord}\n\n`;
  });

  return text.trimEnd();
};

const formatCreativeWritingAnswers = (parsed, chapterTitle) => {
  const prompts = parsed?.prompts || [];
  if (!prompts.length) {
    const title = chapterTitle || 'this topic';
    return `Creative Writing - Topic: "${title}"`;
  }

  let text = '';
  prompts.forEach((prompt, idx) => {
    const title = prompt.title || `Prompt ${idx + 1}`;
    const description = prompt.description || '';

    if (idx > 0) {
      text += '\n';
    }

    text += `Prompt ${idx + 1}: ${title}\n`;

    const rawSentences = description
      .split(/(?<=[.!?])\s+/)
      .map(s => s.trim())
      .filter(Boolean);

    if (!rawSentences.length) {
      text += '\n';
      return;
    }

    rawSentences.forEach(sentence => {
      text += `${sentence}\n`;
    });
  });

  return text.trimEnd();
};

// TENSES answers:
// Show both English and language sentences for each item
const formatTensesAnswers = (parsed) => {
  const groups = Array.isArray(parsed?.groups) ? parsed.groups : [];
  const sentences = groups.flatMap(g => Array.isArray(g.sentences) ? g.sentences : []);
  const valid = sentences.filter(s => s && (((s.englishText || '').trim()) || ((s.languageText || '').trim())));

  if (!valid.length) return 'No sentences available for this activity.';

  let text = '';
  valid.forEach((s, idx) => {
    const en = s.englishText || '';
    const lang = s.languageText || '';
    text += `${idx + 1}. EN: ${en}\n`;
    text += `   LANG: ${lang}\n\n`;
  });

  return text.trimEnd();
};

// Default formatter if we don't have a specific one
const formatDefaultPractice = (parsed) => {
  if (!parsed) return 'No practice content available for this activity.';
  try {
    return JSON.stringify(parsed, null, 2);
  } catch (e) {
    return 'No practice content available for this activity.';
  }
};

export const getAnswerSheetContentForActivity = (activity, chapterTitle, chapterSentences) => {
  if (!activity || !activity.baseContent) {
    return 'No content available for this activity.';
  }

  let parsed;
  try {
    if (typeof activity.baseContent === 'string') {
      parsed = JSON.parse(activity.baseContent);
    } else if (typeof activity.baseContent === 'object') {
      parsed = activity.baseContent;
    } else {
      return 'No content available for this activity.';
    }
  } catch (e) {
    // If parsing fails for a string, fall back to showing the raw string
    if (typeof activity.baseContent === 'string') {
      return activity.baseContent;
    }
    return 'No content available for this activity.';
  }

  switch (activity.activityType) {
    case 'WORD_MEANING':
      return formatWordMeaningAnswers(parsed);
    case 'WORD_PRACTICE':
      return '';
    case 'DUAL_MEANING':
      return formatDualMeaningAnswers(parsed);
    case 'MATCH':
      return formatMatchAnswers(parsed);
    case 'OPPOSITES':
      return formatOppositesAnswers(parsed);
    case 'WHO_SAID_TO_WHOM':
      return formatWhoSaidToWhomAnswers(parsed);
    case 'QUESTION_ANSWERS':
      return formatQuestionAnswersAnswers(parsed);
    case 'LONG_QUESTION_ANSWERS':
      return formatLongQuestionAnswersAnswers(parsed);
    case 'FILL_IN_BLANK_MCQ':
      return formatFillInBlankMcqAnswers(parsed);
    case 'NUMBERS':
      return formatNumbersAnswers(parsed);
    case 'GENDERS':
      return formatGendersAnswers(parsed);
    case 'CHANGE_GENDERS':
      return formatChangeGendersAnswers(parsed);
    case 'TENSES':
      return formatTensesAnswers(parsed);
    case 'SINGULAR_PLURAL':
      return formatSingularPluralAnswers(parsed);
    case 'MAKE_SENTENCES':
      return formatMakeSentencesAnswers(parsed);
    case 'CREATIVE_WRITING': {
      // If chapter sentences are provided, use them as the answer sheet content
      if (Array.isArray(chapterSentences) && chapterSentences.length > 0) {
        const lines = chapterSentences
          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
          .map(s => s.languageText || s.englishText || '')
          .filter(Boolean);
        if (!lines.length) {
          return formatCreativeWritingAnswers(parsed, chapterTitle);
        }
        return lines.join('\n');
      }
      return formatCreativeWritingAnswers(parsed, chapterTitle);
    }
    default:
      return formatDefaultPractice(parsed);
  }
};

export const getPracticeSheetContentForActivity = (activity, chapterTitle) => {
  if (!activity || !activity.baseContent) {
    return 'No content available for this activity.';
  }

  let parsed;
  try {
    if (typeof activity.baseContent === 'string') {
      parsed = JSON.parse(activity.baseContent);
    } else if (typeof activity.baseContent === 'object') {
      parsed = activity.baseContent;
    } else {
      return 'No content available for this activity.';
    }
  } catch (e) {
    // If parsing fails for a string, fall back to showing the raw string
    if (typeof activity.baseContent === 'string') {
      return activity.baseContent;
    }
    return 'No content available for this activity.';
  }

  switch (activity.activityType) {
    case 'WORD_MEANING':
      return formatWordMeaningPractice(parsed);
    case 'WORD_PRACTICE':
      return '';
    case 'DUAL_MEANING':
      return formatDualMeaningPractice(parsed);
    case 'MATCH':
      return formatMatchPractice(parsed);
    case 'OPPOSITES':
      return formatOppositesPractice(parsed);
    case 'WHO_SAID_TO_WHOM':
      return formatWhoSaidToWhomPractice(parsed);
    case 'QUESTION_ANSWERS':
      return formatQuestionAnswersPractice(parsed, 3);
    case 'LONG_QUESTION_ANSWERS':
      return formatLongQuestionAnswersPractice(parsed);
    case 'CREATIVE_WRITING':
      return formatCreativeWritingPractice(chapterTitle);
    case 'FILL_IN_BLANK_MCQ':
      return formatFillInBlankMcqPractice(parsed);
    case 'NUMBERS':
      return formatNumbersPractice(parsed);
    case 'GENDERS':
      return formatGendersPractice(parsed);
    case 'CHANGE_GENDERS':
      return formatChangeGendersPractice(parsed);
    case 'TENSES':
      return formatTensesPractice(parsed);
    case 'SINGULAR_PLURAL':
      return formatSingularPluralPractice(parsed);
    case 'MAKE_SENTENCES':
      return formatMakeSentencesPractice(parsed);
    default:
      return formatDefaultPractice(parsed);
  }
};

export default getPracticeSheetContentForActivity;
